import React, { useEffect, useRef, useState } from "react";
import { Link2, Check, AlertTriangle, X, Loader2, MapPin, Bed, Wand2, Search, Pencil, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { fmtEUR } from "@/pages/cotizador/shared";

const CONFIDENCE = {
  high: { txt: "Match alto", cls: "bg-emerald-600 text-white" },
  medium: { txt: "Match medio", cls: "bg-amber-200 text-amber-900" },
  low: { txt: "Match dudoso", cls: "bg-orange-200 text-orange-900" },
  manual: { txt: "Manual", cls: "bg-primary text-primary-foreground" },
};

function layoutSummary(layout) {
  if (!layout || layout.length === 0) return "";
  const counts = new Map();
  for (const r of layout) counts.set(r.room_type, (counts.get(r.room_type) || 0) + 1);
  return [...counts.entries()].map(([t, n]) => (n > 1 ? `${n}× ${t}` : t)).join(" + ");
}

function InlineMatchPicker({ initialQuery, dayCity, type, onPick, onClose }) {
  const isHotel = type === "alojamiento";
  const [q, setQ] = useState(initialQuery || "");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const h = setTimeout(async () => {
      setBusy(true);
      const params = { q: (q || "").trim(), limit: 8 };
      if (isHotel) params.type = "alojamiento";
      if (dayCity) params.city = dayCity;
      try {
        const { data } = await api.get("/products", { params });
        if (!cancelled) { setResults(data.items || []); setBusy(false); }
      } catch (_e) { if (!cancelled) { setResults([]); setBusy(false); } }
    }, 220);
    return () => { cancelled = true; clearTimeout(h); };
  }, [q, dayCity, isHotel]);

  return (
    <div className="border-t bg-muted/40 px-3 py-2" data-testid="match-picker">
      <div className="mb-2 flex items-center gap-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={dayCity ? `Buscar en ${dayCity}…` : "Buscar en el catálogo…"} className="h-7 flex-1 text-sm" data-testid="match-picker-input" />
        <button onClick={onClose} className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground" data-testid="match-picker-close">Cerrar</button>
      </div>
      {busy && <div className="py-2 text-xs text-muted-foreground">Buscando…</div>}
      {!busy && results.length === 0 && <div className="py-2 text-xs italic text-muted-foreground">Sin resultados. Prueba con menos palabras o quita el filtro de ciudad.</div>}
      {!busy && results.length > 0 && (
        <div className="max-h-56 space-y-1 overflow-y-auto">
          {results.map((r) => {
            const total = Number(r.cost_tax_incl) || Number(r.cost_tax_excl) || 0;
            return (
              <button key={r.id} onClick={() => onPick(r)} data-testid={`match-pick-${r.id}`} className="flex w-full items-center gap-2 rounded border bg-card px-2.5 py-1.5 text-left text-sm hover:border-primary hover:bg-accent">
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">{r.name}</div>
                  <div className="truncate text-[11px] text-muted-foreground">{[r.provider_name, r.city, r.country].filter(Boolean).join(" · ")}</div>
                </div>
                <div className="shrink-0 text-right text-xs tabular-nums">
                  <div className="font-semibold">{fmtEUR(total)}{isHotel ? "/n" : ""}</div>
                  {!isHotel && r.pax ? <div className="text-[10px] text-muted-foreground">{r.pax} pax</div> : null}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CotTravefyImport({ open, onClose, onCreated }) {
  const [step, setStep] = useState("url");
  const [url, setUrl] = useState("");
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);
  useEffect(() => { if (open) { setStep("url"); setUrl(""); setPreview(null); setError(null); setPickerOpen(null); } }, [open]);

  const start = async () => {
    if (!url.includes("travefy.com")) { toast.error("La URL debe ser de travefy.com"); return; }
    setError(null); setStep("running");
    try {
      const { data } = await api.post("/travefy/import/preview", { url });
      pollRef.current = setInterval(() => poll(data.job_id), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Error iniciando importación"); setStep("url");
    }
  };

  const poll = async (jid) => {
    try {
      const { data } = await api.get(`/travefy/import/preview/${jid}`);
      if (data.status === "done") {
        clearInterval(pollRef.current); pollRef.current = null;
        // Extraer título limpio del `trip_name` (formato típico: "Título - Cliente").
        const rawTitle = (data.preview.trip_name || "").trim();
        let itTitle = rawTitle;
        if (rawTitle.includes(" - ")) {
          const parts = rawTitle.split(" - ");
          const tail = parts[parts.length - 1];
          if (tail && tail.split(/\s+/).length <= 4) itTitle = parts.slice(0, -1).join(" - ").trim();
        }
        setPreview({
          ...data.preview,
          itinerary_title: itTitle,
          days: (data.preview.days || []).map((d) => ({ ...d, items: (d.items || []).map((it) => ({ ...it, excluded: false })) })),
          hotels: (data.preview.hotels || []).map((h) => ({ ...h, excluded: false })),
        });
        setStep("preview");
      } else if (data.status === "error") {
        clearInterval(pollRef.current); pollRef.current = null;
        setError(data.error || "Error desconocido"); setStep("url");
      }
    } catch (e) {
      if (e?.response?.status === 404) { clearInterval(pollRef.current); pollRef.current = null; setError("Job no encontrado"); setStep("url"); }
    }
  };

  const toggleItem = (di, ii) => setPreview((p) => {
    const days = [...p.days]; const items = [...days[di].items];
    items[ii] = { ...items[ii], excluded: !items[ii].excluded }; days[di] = { ...days[di], items }; return { ...p, days };
  });
  const toggleHotel = (hi) => setPreview((p) => {
    const hotels = [...p.hotels]; hotels[hi] = { ...hotels[hi], excluded: !hotels[hi].excluded }; return { ...p, hotels };
  });

  const pickItemMatch = (di, ii, r) => {
    setPreview((p) => {
      const days = [...p.days]; const items = [...days[di].items];
      items[ii] = { ...items[ii], type: "actividad", match: {
        product_id: r.id, name: r.name, pax: r.pax || 1, city: r.city, country: r.country, provider_name: r.provider_name,
        cost_tax_excl: r.cost_tax_excl || 0, cost_tax_incl: r.cost_tax_incl || r.cost_tax_excl || 0, currency: r.currency || "EUR", confidence: "manual",
      } };
      days[di] = { ...days[di], items }; return { ...p, days };
    });
    setPickerOpen(null); toast.success("Match vinculado");
  };
  const unlinkItem = (di, ii) => setPreview((p) => {
    const days = [...p.days]; const items = [...days[di].items]; items[ii] = { ...items[ii], match: null }; days[di] = { ...days[di], items }; return { ...p, days };
  });
  const pickHotelMatch = (hi, r) => {
    setPreview((p) => {
      const hotels = [...p.hotels];
      hotels[hi] = { ...hotels[hi], match: {
        product_id: r.id, name: r.name, city: r.city, country: r.country,
        cost_tax_excl: r.cost_tax_excl || 0, cost_tax_incl: r.cost_tax_incl || r.cost_tax_excl || 0, currency: r.currency || "EUR", confidence: "manual",
      } };
      return { ...p, hotels };
    });
    setPickerOpen(null); toast.success("Hotel vinculado");
  };
  const unlinkHotel = (hi) => setPreview((p) => { const hotels = [...p.hotels]; hotels[hi] = { ...hotels[hi], match: null }; return { ...p, hotels }; });

  const confirm = async () => {
    setConfirming(true);
    try {
      const { data } = await api.post("/travefy/import/confirm", preview);
      toast.success("Cotización creada desde Travefy");
      onClose(); onCreated?.(data.id);
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error(Array.isArray(det) ? det.map((d) => `${(d.loc || []).join(".")}: ${d.msg}`).join(" · ") : (det || e?.message || "Error creando cotización"), { duration: 10000 });
    } finally { setConfirming(false); }
  };

  const stats = preview ? (() => {
    let matched = 0, unmatched = 0;
    (preview.days || []).forEach((d) => (d.items || []).forEach((it) => { if (it.excluded || it.is_note) return; if (it.match) matched++; else unmatched++; }));
    const hMatched = (preview.hotels || []).filter((h) => !h.excluded && h.match).length;
    const hMissing = (preview.hotels || []).filter((h) => !h.excluded && !h.match).length;
    return { matched, unmatched, hMatched, hMissing };
  })() : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto p-0" data-testid="travefy-modal">
        <div className="sticky top-0 z-10 flex items-center gap-3 border-b bg-background px-5 py-4">
          <Wand2 className="h-5 w-5 text-primary" />
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Importar desde Travefy</div>
            <div className="font-heading text-lg font-bold">
              {step === "url" && "Pega el link del itinerario"}
              {step === "running" && "Leyendo y matcheando con tu catálogo…"}
              {step === "preview" && "Revisa antes de crear"}
            </div>
          </div>
        </div>

        <div className="p-5">
          {step === "url" && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">Lee un itinerario publicado de Travefy y monta una base con las actividades y hoteles que ya tienes en el catálogo. Lo que no esté aparece como «Sin match · revisar precio» para completarlo después.</p>
              <div className="relative">
                <Link2 className="absolute left-3 top-3 h-3.5 w-3.5 text-muted-foreground" />
                <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://travefy.com/trip/itinerary/…" className="pl-9" data-testid="travefy-url-input" onKeyDown={(e) => e.key === "Enter" && url && start()} />
              </div>
              {error && <div className="rounded border border-destructive bg-destructive/5 p-3 text-sm text-destructive" data-testid="travefy-error">{error}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={onClose} data-testid="travefy-cancel">Cancelar</Button>
                <Button onClick={start} disabled={!url} data-testid="travefy-start">Importar</Button>
              </div>
            </div>
          )}

          {step === "running" && (
            <div className="py-16 text-center">
              <Loader2 className="mx-auto mb-4 h-9 w-9 animate-spin text-primary" />
              <div className="font-heading text-lg font-bold">Procesando…</div>
              <div className="mx-auto mt-2 max-w-md text-sm text-muted-foreground">Abriendo Travefy, leyendo cada día y matcheando cada actividad y hotel con tu catálogo. Por la API de Travefy tarda <span className="font-semibold">unos segundos</span> y trae todas las fotos y descripciones; si el itinerario no está publicado se usa el lector lento (20-60 s).</div>
            </div>
          )}

          {step === "preview" && preview && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted/40 p-3">
                <label className="flex flex-col gap-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Título del itinerario (lo verá el cliente)</span>
                  <Input value={preview.itinerary_title ?? preview.trip_name ?? ""} onChange={(e) => setPreview((p) => ({ ...p, itinerary_title: e.target.value }))} placeholder="Ej.: A Sicilian Discovery: Palermo, Trapani & the Aeolian Islands" data-testid="travefy-title-input" />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-0 overflow-hidden rounded-lg border text-sm sm:grid-cols-4">
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Cliente</div><div className="truncate font-semibold">{preview.main_traveler || preview.trip_name || "—"}</div></div>
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Fechas</div><div className="text-xs font-semibold tabular-nums">{preview.start_date || "—"} → {preview.end_date || "—"}</div><div className="text-xs text-muted-foreground">{(preview.days || []).length} días · {preview.num_travelers} pax</div>
                  {preview.source === "travefy_api" && <div className="mt-0.5 text-[10px] text-emerald-700" data-testid="travefy-api-badge">API Travefy · {preview.photos || 0} fotos · {preview.descriptions || 0} descripciones{Object.keys(preview.info_documents || {}).length ? ` · ${Object.keys(preview.info_documents).length} documentos` : ""}</div>}</div>
                <div className="border-r p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Actividades</div><div className="font-semibold"><span className="text-emerald-600">{stats.matched}</span>{stats.unmatched > 0 && <span className="text-amber-600"> · {stats.unmatched} sin match</span>}</div></div>
                <div className="p-3"><div className="text-[10px] uppercase tracking-wide text-muted-foreground">Hoteles</div><div className="font-semibold"><span className="text-emerald-600">{stats.hMatched}</span>{stats.hMissing > 0 && <span className="text-amber-600"> · {stats.hMissing} sin match</span>}</div></div>
              </div>

              <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                {(preview.days || []).map((day, di) => (
                  <div key={di} className="overflow-hidden rounded-lg border">
                    <div className="flex items-center gap-2 bg-muted px-3 py-2 text-sm">
                      <span className="text-[10px] font-bold uppercase tracking-wider">Día {day.day}</span>
                      <span className="tabular-nums text-muted-foreground">{day.date}</span>
                      {day.city && <span className="inline-flex items-center gap-1 text-muted-foreground"><MapPin className="h-3 w-3" />{day.city}</span>}
                    </div>
                    {(day.items || []).length === 0 ? <div className="px-3 py-2 text-xs italic text-muted-foreground">Sin actividades.</div> : (day.items || []).map((it, ii) => {
                      const m = it.match; const conf = m ? CONFIDENCE[m.confidence] : null; const key = `day:${di}:${ii}`; const isOpen = pickerOpen === key;
                      return (
                        <React.Fragment key={ii}>
                          <div className={`flex items-center gap-2 border-t px-3 py-2 ${it.excluded ? "opacity-40" : ""}`} data-testid={`travefy-item-${di}-${ii}`}>
                            <input type="checkbox" checked={!it.excluded} onChange={() => toggleItem(di, ii)} className="cursor-pointer" data-testid={`travefy-item-toggle-${di}-${ii}`} />
                            <span className="w-16 shrink-0 text-center text-[9px] uppercase tracking-wider text-muted-foreground">{it.is_note ? "nota" : it.type === "alojamiento" ? "aloj." : "activ."}</span>
                            {(it.images || []).length > 0 && <img src={it.images[0]} alt="" className="h-8 w-12 shrink-0 rounded object-cover" loading="lazy" data-testid={`travefy-item-photo-${di}-${ii}`} />}
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm" title={it.travefy_name}>{it.travefy_name}{(it.images || []).length > 1 && <span className="ml-1 text-[10px] text-muted-foreground">+{it.images.length - 1} fotos</span>}</div>
                              {it.is_note ? (
                                <div className="truncate text-xs text-muted-foreground">Texto y fotos del día · se añade como nota (sin precio)</div>
                              ) : m ? (
                                <div className="flex items-center gap-2 truncate text-xs text-muted-foreground"><Check className="h-3 w-3 shrink-0 text-emerald-600" /><span className="truncate">{m.name}</span><span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${conf.cls}`}>{conf.txt}</span></div>
                              ) : (
                                <div className="flex items-center gap-2 truncate text-xs text-amber-600"><AlertTriangle className="h-3 w-3 shrink-0" /><span>Sin match · revisar precio después</span></div>
                              )}
                            </div>
                            <div className="shrink-0 whitespace-nowrap text-right text-sm font-semibold tabular-nums">{m ? fmtEUR(m.cost_tax_incl || 0) : "—"}</div>
                            <div className="flex shrink-0 items-center gap-1">
                              <button onClick={() => setPickerOpen(isOpen ? null : key)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title={m ? "Cambiar match" : "Vincular al catálogo"} data-testid={`travefy-item-pick-${di}-${ii}`}>{m ? <Pencil className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}</button>
                              {m && <button onClick={() => unlinkItem(di, ii)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" title="Quitar match" data-testid={`travefy-item-unlink-${di}-${ii}`}><X className="h-3.5 w-3.5" /></button>}
                            </div>
                          </div>
                          {isOpen && <InlineMatchPicker initialQuery={it.travefy_name} dayCity={day.city} type={it.type} onPick={(r) => pickItemMatch(di, ii, r)} onClose={() => setPickerOpen(null)} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                ))}

                {(preview.hotels || []).length > 0 && (
                  <div className="overflow-hidden rounded-lg border">
                    <div className="flex items-center gap-2 bg-primary px-3 py-2 text-[11px] uppercase tracking-wider text-primary-foreground"><Bed className="h-3.5 w-3.5" /> Alojamientos</div>
                    {(preview.hotels || []).map((h, hi) => {
                      const m = h.match; const conf = m ? CONFIDENCE[m.confidence] : null; const key = `hotel:${hi}`; const isOpen = pickerOpen === key;
                      return (
                        <React.Fragment key={hi}>
                          <div className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 ${h.excluded ? "opacity-40" : ""}`} data-testid={`travefy-hotel-${hi}`}>
                            <input type="checkbox" checked={!h.excluded} onChange={() => toggleHotel(hi)} className="cursor-pointer" data-testid={`travefy-hotel-toggle-${hi}`} />
                            <div className="min-w-0 flex-1">
                              <div className="truncate text-sm font-semibold" title={h.travefy_name}>{h.travefy_name}</div>
                              {m ? (
                                <div className="flex items-center gap-2 truncate text-xs text-muted-foreground"><Check className="h-3 w-3 shrink-0 text-emerald-600" /><span className="truncate">{m.name}</span><span className={`rounded px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${conf.cls}`}>{conf.txt}</span></div>
                              ) : (
                                <div className="flex items-center gap-2 truncate text-xs text-amber-600"><AlertTriangle className="h-3 w-3 shrink-0" /><span>Sin match · vincúlalo o complétalo en el builder</span></div>
                              )}
                              {(h.room_type || h.room_type_raw) && (
                                <div className="mt-0.5 flex items-center gap-1.5 truncate text-[10px] text-muted-foreground">
                                  <Bed className="h-2.5 w-2.5 shrink-0" />
                                  <span className="font-semibold uppercase tracking-wider text-primary">{h.rooms_layout && h.rooms_layout.length > 0 ? layoutSummary(h.rooms_layout) : h.room_type}</span>
                                  {h.room_type_raw && <span className="truncate italic">· &quot;{h.room_type_raw}&quot;</span>}
                                </div>
                              )}
                            </div>
                            <div className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">{h.check_in} → {h.check_out}</div>
                            <div className="whitespace-nowrap text-right text-sm font-semibold tabular-nums">{m ? `${fmtEUR(m.cost_tax_incl || 0)}/n` : "—"}</div>
                            <div className="flex shrink-0 items-center gap-1">
                              <button onClick={() => setPickerOpen(isOpen ? null : key)} className="rounded p-1.5 text-muted-foreground hover:bg-muted" title={m ? "Cambiar hotel" : "Vincular al catálogo"} data-testid={`travefy-hotel-pick-${hi}`}>{m ? <Pencil className="h-3.5 w-3.5" /> : <LinkIcon className="h-3.5 w-3.5" />}</button>
                              {m && <button onClick={() => unlinkHotel(hi)} className="rounded p-1.5 text-muted-foreground hover:text-destructive" title="Quitar match" data-testid={`travefy-hotel-unlink-${hi}`}><X className="h-3.5 w-3.5" /></button>}
                            </div>
                          </div>
                          {isOpen && <InlineMatchPicker initialQuery={h.travefy_name} dayCity={h.city} type="alojamiento" onPick={(r) => pickHotelMatch(hi, r)} onClose={() => setPickerOpen(null)} />}
                        </React.Fragment>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between border-t pt-3">
                <div className="text-xs text-muted-foreground">Después podrás afinar precios, añadir habitaciones y completar lo que falte en el constructor.</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={onClose}>Cancelar</Button>
                  <Button onClick={confirm} disabled={confirming} data-testid="travefy-confirm">{confirming && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Crear cotización</Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
